import React from 'react';
import { fmtDateTime } from '../../utils/formatters';
import { BoletoCardFilter, BoletoStats } from './boletoCapabilities';
import { TONE_TEXT } from '../../utils/statusColors';
import IconeDoHub from '../shared/Icon';
import { CartaoDoTopo } from '../shared/lista/PecasDaLista';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

interface CardProps {
  label: string;
  /** Número, ou "—"/"…" quando a contagem não é conhecida (nunca um 0 que ninguém contou). */
  count: number | string;
  value?: number;
  hint?: React.ReactNode;
  active: boolean;
  onClick: () => void;
  /** K1: a barra e o ícone de quem pede ação (o "Atenção" com algo dentro). */
  alerta?: boolean;
}

// K1 (12/10/2026): o número sem matiz e o escolhido sem o anel laranja; quem pede ação ganha a barra e o ícone. 22/10/2026
// (padrão das listas no Financeiro): o cartão é o CartaoDoTopo, com a superfície da Central e o escolhido na barra primary.
const Card: React.FC<CardProps> = ({ label, count, value, hint, active, onClick, alerta }) => (
  <CartaoDoTopo
    rotulo={<>{alerta && <IconeDoHub name="alerta" className={`w-4 h-4 inline-block mr-1.5 align-[-3px] ${TONE_TEXT.warning}`} />}{label}</>}
    valor={count}
    sub={value !== undefined || hint ? <>{value !== undefined && <span className="block">R$ {fmt(value)}</span>}{hint}</> : undefined}
    ativo={active}
    barra={alerta ? 'warning' : null}
    onClick={onClick}
  />
);

export interface Divergencias {
  emissaoTravada: any[];
  pagaComTituloVivo: any[];
  abertaSemInstrumento: any[];
  vencidosComTituloVivo: any[];
  webhookSilencioso: boolean;
  ultimoWebhook: string | null;
}

interface Props {
  stats: BoletoStats;
  cardFilter: BoletoCardFilter;
  onFilter: (f: BoletoCardFilter) => void;
  /** Divergências de TODA a base — "Atenção" não respeita competência. */
  divergencias: Divergencias | null;
  /** Não null: a varredura da base (divergências) NÃO rodou, e o cartão "Atenção" não pode afirmar nada. */
  divergenciasErro?: string | null;
  onFocusAtencao: () => void;
  lastRetorno: any;
  /** C4: não null = a leitura do último retorno falhou. */
  lastRetornoErro?: string | null;
  /** C3: há lista lida do recorte; sem ela, as contagens são "—", nunca 0. */
  lido?: boolean;
}

/**
 * Cards do topo. Saíram "Pendentes" e "Registrados" (zero ocorrências em 1.073
 * faturas — dois cards decorativos) e entraram "Vencidos" com aging e "Atenção",
 * que expõem o que a tela não mostrava: 42 faturas vencidas e as faturas presas
 * em estados que exigem ação humana.
 */
const BoletoStatsCards: React.FC<Props> = ({ stats, cardFilter, onFilter, divergencias, divergenciasErro, onFocusAtencao, lastRetorno, lastRetornoErro, lido = true }) => {
  // C3 (12/10/2026): sem a lista do recorte, os cinco cartões diziam 0 e R$ 0,00 ao lado do erro. Agora "—".
  const n = (x: number) => (lido ? x : '—');
  const r = (x: number) => (lido ? x : undefined);
  const toggle = (f: BoletoCardFilter) => () => onFilter(cardFilter === f ? 'all' : f);
  const v = stats.vencidos;

  // As faturas que exigem ação humana quase nunca são do mês corrente (hoje as
  // cinco existentes são de março a junho). Contar só o período selecionado
  // faria o card mostrar zero justamente no caso que ele existe para expor.
  const d = divergencias;
  // ⛔ SEM A VARREDURA, NUNCA UM NÚMERO (08/10/2026). Antes, sem as divergências o cartão caía para a contagem DO
  // PERÍODO e, com ela em 0, dizia "Nada pendente": o alarme da base inteira afirmava tudo-bem sem ter olhado.
  const atencaoGlobal: number | null = d
    ? d.emissaoTravada.length + d.pagaComTituloVivo.length + d.abertaSemInstrumento.length
    : null;
  const detalheAtencao = d
    ? [
        d.emissaoTravada.length ? `${d.emissaoTravada.length} emissão travada` : '',
        d.pagaComTituloVivo.length ? `${d.pagaComTituloVivo.length} paga com título vivo` : '',
        d.abertaSemInstrumento.length ? `${d.abertaSemInstrumento.length} sem instrumento de cobrança` : '',
      ].filter(Boolean).join(' · ')
    : '';

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
      <Card
        label="Total Faturas" count={n(stats.total)} active={cardFilter === 'all'}
        onClick={() => onFilter('all')}
      />
      <Card
        label="A emitir" count={n(stats.aEmitir.count)} value={r(stats.aEmitir.value)}
        active={cardFilter === 'a_emitir'} onClick={toggle('a_emitir')}
      />
      <Card
        label="Em aberto" count={n(stats.emAberto.count)} value={r(stats.emAberto.value)}
        active={cardFilter === 'em_aberto'} onClick={toggle('em_aberto')}
      />
      <Card
        label="Vencidos" count={n(v.count)} value={r(v.value)}
        active={cardFilter === 'vencidos'} onClick={toggle('vencidos')}
        hint={
          lido && v.count > 0 ? (
            <span className="block text-[10px] text-slate-500 mt-1 tabular-nums">
              {/* K3: o aging em peso, sem as três cores */}
              <span className="font-semibold text-slate-300">{v.ate7}</span> até 7d ·{' '}
              <span className="font-semibold text-slate-300">{v.ate30}</span> até 30d ·{' '}
              <span className="font-semibold text-slate-300">{v.mais30}</span> +30d
            </span>
          ) : undefined
        }
      />
      <Card
        label="Pagos" count={n(stats.pagos.count)} value={r(stats.pagos.value)}
        active={cardFilter === 'pagos'} onClick={toggle('pagos')}
        hint={
          lido && stats.pagos.juros > 0 ? (
            <span className="block text-[10px] text-amber-400/80 mt-1">+R$ {fmt(stats.pagos.juros)} juros/multa</span>
          ) : undefined
        }
      />
      <Card
        label="Atenção" count={atencaoGlobal ?? (divergenciasErro ? '—' : '…')}
        active={cardFilter === 'atencao'}
        alerta={atencaoGlobal !== null && atencaoGlobal > 0}
        onClick={() => (cardFilter === 'atencao' ? onFilter('all') : onFocusAtencao())}
        hint={
          <span className="block text-[10px] text-slate-500 mt-1 leading-tight" title={detalheAtencao}>
            {atencaoGlobal === null
              ? (divergenciasErro
                ? <span data-atencao="nao-varreu">Não consegui varrer a base ({divergenciasErro}): o que precisa de atenção é desconhecido</span>
                : 'Varrendo a base…')
              : atencaoGlobal > 0 ? (detalheAtencao || 'Exige conferência manual') : 'Nada pendente'}
            {atencaoGlobal !== null && atencaoGlobal > 0 && <span className="block text-slate-600">em toda a base, não só no período</span>}
          </span>
        }
      />

      {/* Saúde da integração — antes era um card fixo "Sicredi · 748" sem
          informação nenhuma. Ocupa a linha inteira abaixo dos KPIs. */}
      <div className="col-span-2 md:col-span-3 xl:col-span-6 flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 bg-central-cartao rounded-lg border border-central-borda text-xs">
        <span className="text-slate-400 font-medium">Sicredi · 748</span>
        <span className="text-slate-600">|</span>
        <span className={d?.webhookSilencioso ? 'text-amber-400' : 'text-slate-400'}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${
            // E1: o normal (webhook vivo) é neutro; o ponto só ganha cor quando pede atenção.
            !d ? 'bg-slate-600' : d.webhookSilencioso ? 'bg-amber-400' : 'bg-slate-400'
          }`} />
          Último webhook: {d?.ultimoWebhook ? fmtDateTime(d.ultimoWebhook) : divergenciasErro ? '— (não consegui ler)' : '—'}
          {d?.webhookSilencioso && ' (silencioso há +48h)'}
        </span>
        <span className="text-slate-600">|</span>
        <span className="text-slate-400">
          Último retorno CNAB: {lastRetorno?.importedAt ? fmtDateTime(lastRetorno.importedAt) : lastRetornoErro ? '— (não consegui ler)' : '—'}
        </span>
        <span className="text-slate-600">|</span>
        {/* C6 (12/10/2026): os horários eram texto fixo, sem ler os crons de onde eles vivem; a faixa não afirma hora. */}
        <span className="text-slate-500">Conciliação automática com o Sicredi</span>
      </div>
    </div>
  );
};

export default BoletoStatsCards;
